import React, { useEffect, useMemo, useState } from 'react';
import { Box, InputAdornment, Stack, TextField, Typography, Menu, MenuItem, IconButton, Button } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import useOpenModal from '@hooks/useOpenModal';
import useConfirmationModal from '@hooks/useConfirmationModal';
import { DistributionCenter } from '@api/DistributionCenters/DistributionCenter';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import DistributionCenterService from '@api/DistributionCenters/DistributionCenterCtrl';
import { ModalSaveDistributionCenter } from '@screens/Cedis/components/ModalAddDistributionCenter';
import { ModalViewCedis } from '@screens/Cedis/components/ModalViewCedis';
import { showAlertMessage } from '@utils/Alerts/Message';
import { useNavigate } from 'react-router-dom';
import Led from '@components/Utilities/Led';
import { useDispatch } from 'react-redux';
import { setDistributionCenterSelected } from '@store/authStore';
import Grid from '@mui/material/Grid';
import SearchIcon from '@mui/icons-material/Search';
import { refreshDistributionCenters } from '@utils/Catalogs';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import { ModalConfirmation } from '@components/Utilities/Modals/ModalConfirmation';

const CedisList: React.FunctionComponent = () => {
    const navigation = useNavigate();
    const distributionCenterService = useMemo(() => new DistributionCenterService(), []);
    const [distributionCenters, setDistributionCentersData] = useState<DistributionCenter[]>([]);
    const [currentDistributionCenter, setCurrentDistributionCenter] = useState<DistributionCenter | undefined>(
        undefined
    );
    const modalAddDistributionCenter = useOpenModal();
    const modalConfirmation = useConfirmationModal();

    const [filters, setFilters] = useState({
        search: ''
    });

    const dispatch = useDispatch();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedCedis, setSelectedCedis] = useState<DistributionCenter | undefined>(undefined);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: DistributionCenter) => {
        setAnchorEl(event.currentTarget);
        setSelectedCedis(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleView = () => {
        handleMenuClose();
        setIsViewModalOpen(true);
    };

    const handleEdit = () => {
        handleMenuClose();
        setCurrentDistributionCenter(selectedCedis);
        modalAddDistributionCenter.toggle();
    };

    const handleToggleStatus = async () => {
        handleMenuClose();
        if (selectedCedis?.id) {
            showLoader();
            try {
                await distributionCenterService.toggleStatus(selectedCedis.id);
                showAlertMessage(`CEDIS ${selectedCedis.status ? 'inactivado' : 'activado'} exitosamente`);
                await getData();
                await refreshDistributionCenters();
            } catch (error) {
                showAlertError(error);
            }
            hideLoader();
        }
    };

    const handleViewInventory = () => {
        handleMenuClose();
        if (selectedCedis?.id) {
            dispatch(setDistributionCenterSelected(selectedCedis));
            navigation('inventory');
        }
    };

    const handleViewOrders = () => {
        handleMenuClose();
        if (selectedCedis?.id) {
            dispatch(setDistributionCenterSelected(selectedCedis));
            navigation('orders');
        }
    };

    const handleDeleteClick = () => {
        handleMenuClose();
        if (selectedCedis) {
            modalConfirmation.setModalData(
                'Eliminar Cedis',
                `¿Estás seguro de que deseas eliminar el CEDIS "${selectedCedis.name}"? Esta acción no se puede deshacer.`
            );
        }
    };

    const handleDeleteConfirm = async () => {
        if (selectedCedis?.id) {
            showLoader();
            try {
                await distributionCenterService.deleteDistributionCenter(selectedCedis.id);
                showAlertMessage('Cedis eliminado exitosamente');
                await getData();
                await refreshDistributionCenters();
            } catch (error) {
                showAlertError(error);
            }
            hideLoader();
        }
    };

    // Local working set for this management table. The global slice (read by other
    // screens) is refreshed separately, only after mutations.
    const getData = async () => {
        showLoader();
        try {
            const data = await distributionCenterService.getDistributionCenters();
            setDistributionCentersData(data);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onOpenModalAddDistributionCenter = () => {
        setCurrentDistributionCenter(undefined);
        modalAddDistributionCenter.toggle();
    };

    const onAddDistributionCenter = async (distributionCenter: DistributionCenter) => {
        showLoader();
        try {
            await distributionCenterService.saveDistributionCenter(distributionCenter);
            showAlertMessage('Cedis guardado exitosamente');
            await getData();
            await refreshDistributionCenters();
            modalAddDistributionCenter.toggle();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, []);

    const columns = useMemo<ResponsiveGridColDef<DistributionCenter>[]>(
        () => [
            {
                field: 'name',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        CEDIS
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography fontWeight={600}>{row.name}</Typography>
            },
            {
                field: 'phone',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Teléfono
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.phone}</Typography>
            },
            {
                field: 'email',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Correo
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.email}</Typography>
            },
            {
                field: 'status',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Estado
                    </Typography>
                ),
                renderCell: ({ row }) => <Led value={row.status} />
            },
            {
                field: 'actions',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => <SettingsIcon sx={{ color: '#809FB8' }} />,
                renderCell: ({ row }) => (
                    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreVertIcon />
                    </IconButton>
                )
            }
        ],
        []
    );

    return (
        <Stack height="100%" spacing={2}>
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={6} />
                    <Grid item xs={12} md={3} textAlign="right">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onOpenModalAddDistributionCenter}
                            endIcon={<AddIcon />}
                        >
                            Nuevo CEDIS
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Buscar CEDIS"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

            <ResponsiveDataGrid
                searchValue={filters.search}
                rows={distributionCenters}
                columns={columns}
                onCellDoubleClick={(cel) => {
                    setSelectedCedis(cel.row as DistributionCenter);
                    setIsViewModalOpen(true);
                }}
            />

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleView}>Ver</MenuItem>
                <MenuItem onClick={handleEdit}>Editar</MenuItem>
                <MenuItem onClick={handleToggleStatus}>{selectedCedis?.status ? 'Desactivar' : 'Activar'}</MenuItem>
                <MenuItem onClick={handleViewInventory}>Ver inventario</MenuItem>
                <MenuItem onClick={handleViewOrders}>Ver pedidos</MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    Eliminar
                </MenuItem>
            </Menu>

            <ModalSaveDistributionCenter
                modalAddDistributionCenter={modalAddDistributionCenter}
                distributionCenter={currentDistributionCenter}
                onAddDistributionCenter={onAddDistributionCenter}
            />

            <ModalViewCedis
                modalOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                distributionCenter={selectedCedis}
            />

            <ModalConfirmation modalConfirmation={modalConfirmation} onConfirmation={handleDeleteConfirm} />
        </Stack>
    );
};

export default CedisList;
